import { cn } from "@/utils/cn";
import { Dialog, Transition } from "@headlessui/react";
import clsx from "clsx";
import { Fragment, useMemo, useState } from "react";
import { HiX } from "react-icons/hi";

export interface IModalConfig {
  size?: "sm" | "base" | "lg" | "xl" | "md";
  title?: string;
  titleClose?: boolean;
  closeOutsideClick?: boolean;
  onCloseCallback?: () => void;
  bgClass?: boolean;
  childrenClassName?: string;
}

interface IModalChildProps {
  onClose: () => void;
}

interface Props extends IModalConfig {
  children: (props: IModalChildProps) => React.ReactNode;
  onClose: () => void;
  onCloseCallback?: () => void;
}

const MAP_SIZE_CLASS = {
  sm: "w-full max-w-[360px]",
  base: "w-full max-w-[500px]",
  md: "w-full max-w-[600px]",
  lg: "w-full max-w-[800px]",
  xl: "w-full max-w-[1140px]"
};

const doNothing = () => {
  /* do nothing */
};

function Modal({
  children,
  size,
  onClose,
  onCloseCallback,
  title,
  titleClose,
  closeOutsideClick,
  bgClass,
  childrenClassName
}: Props) {
  const [open, setOpen] = useState(true);
  const params = useMemo(
    () => ({
      onClose: () => {
        onCloseCallback?.();
        setOpen(false);
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setOpen]
  );
  return (
    <Transition.Root appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeOutsideClick ? setOpen : doNothing}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={onClose}
        >
          <div className="fixed inset-0 bg-black bg-opacity-60 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={cn(
                  "relative w-full transform rounded-lg border bg-white text-left shadow-modal transition-all",
                  MAP_SIZE_CLASS[size ?? "base"]
                )}
              >
                {(title || titleClose) && (
                  <div
                    className={`flex justify-between rounded-t-lg p-6 text-center ${bgClass ? "bg-primary-base text-white" : "bg-fill-light"}`}
                  >
                    <div className="truncate font-semibold leading-[18px]">{title}</div>
                    {titleClose ? (
                      <button
                        className={clsx(
                          "h-2 w-2",
                          bgClass ? "hover:text-dark-base" : "hover:text-primary-base"
                        )}
                        type="button"
                        title="Modal Close"
                        onClick={params.onClose}
                      >
                        <HiX />
                      </button>
                    ) : null}
                  </div>
                )}
                <div className={clsx("px-5 pb-5", childrenClassName)}>{children(params)}</div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export const ModalFooter = ({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={clsx(
      "-mx-6 -mb-6 mt-6 flex flex-row-reverse justify-start space-x-2 space-x-reverse border-t p-6",
      className
    )}
  >
    {children}
  </div>
);

export default Modal;
